import React, {useEffect, useRef} from "react";
import {
    Box,
    Button,
    Dialog,
    Heading,
    Inline,
    LoadingButton,
    Stack,
    Text,
    usePatronTheme,
} from "@unibuddy/patron";
import {IconEllipsisVertical, IconUserGroup} from "@unibuddy/icons";
import styled from "styled-components";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {useAnalytics} from "@unibuddy/tracking";

import {
    ConversationState,
    useAssistantConversationContext,
} from "../../providers/AssistantConversationProvider";
import {useAssistantInitiationContext} from "../../providers/AssistantInitiationProvider";
import {SHOW_BETA_STYLES} from "../../../../utils/featureFlags";
import {useScreensizeContext} from "../../../../providers/ScreensizeProvider";
import {useMobileMenu} from "../../../../providers/MobileMenuProvider";
import {MobileMenu} from "./MobileMenu";
import {events} from "../../../../analytics/productSpecification";
import {useUniversityContext} from "../../../../providers/UniversityProvider";

export type ChatHeaderContentProps = {
    handleCloseClicked: () => void;
};

const StyledText = styled(Text)`
    letter-spacing: 2px;
`;

export function ChatHeaderContent({handleCloseClicked}: Readonly<ChatHeaderContentProps>) {
    const theme = usePatronTheme();
    const {trackEvent} = useAnalytics();
    const {universitySlug} = useUniversityContext();
    const {isSmallDeviceDimension, isSmallInnerHeight} = useScreensizeContext();
    const {isMobileMenuOpen, setIsMobileMenuOpen} = useMobileMenu();
    const isBetaStyleEnabled = useBooleanFeatureFlag(
        SHOW_BETA_STYLES.key,
        SHOW_BETA_STYLES.defaultValue,
    );
    const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
    const connectMeButtonRef = useRef<HTMLButtonElement | null>(null);
    const {loading} = useAssistantInitiationContext();
    const {onSendMessage, conversationState} = useAssistantConversationContext();

    const isDisabled = loading || conversationState !== ConversationState.USER_CAN_SEND_MESSAGE;

    async function handleConnect() {
        try {
            await onSendMessage("Connect me to the right person");
            trackEvent(events.connectMeButtonClicked.name, {
                product: "Assistant",
                cta: "Connect me",
                universitySlug,
            });
        } catch (e) {
            console.error("Could not send connect me message", e);
        }
    }

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            if (e.data === "chatVisible=true") {
                if (!isSmallDeviceDimension) {
                    connectMeButtonRef.current?.focus();
                } else {
                    mobileMenuButtonRef.current?.focus();
                }
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [isSmallDeviceDimension, isBetaStyleEnabled]);

    return (
        <>
            <Dialog
                isOpen={isMobileMenuOpen}
                onDismiss={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                CloseButton={null}
            >
                <MobileMenu
                    handleCloseClicked={handleCloseClicked}
                    isConnectButtonDisabled={isDisabled}
                />
            </Dialog>
            <Box
                padding={isSmallDeviceDimension ? "small-medium" : "medium-large"}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                w="100%"
                minW="100%"
            >
                <Stack space={isSmallDeviceDimension ? "small" : "small-medium"}>
                    <Inline verticalAlign="center" space="xsmall">
                        <Heading level="2" size="xsmall" color={theme.colors.primaryColor}>
                            Assistant
                        </Heading>
                        {isBetaStyleEnabled && (
                            <Box borderRadius="xxsmall" bgColor="blue100" padding="xsmall">
                                <StyledText size="small" weight="bold" color="blue800">
                                    BETA
                                </StyledText>
                            </Box>
                        )}
                    </Inline>
                    {!isSmallInnerHeight && (
                        <Box
                            display="flex"
                            gap="xsmall"
                            justifyContent="flex-start"
                            alignItems="center"
                        >
                            <Box borderRadius="full" bgColor="green700" w="14px" h="14px" />
                            <Text>Online 24/7</Text>
                        </Box>
                    )}
                </Stack>
                {isSmallDeviceDimension ? (
                    <Button
                        iconOnly
                        round
                        clear
                        size="sm"
                        color="light"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Open menu"
                        ref={mobileMenuButtonRef}
                    >
                        <IconEllipsisVertical />
                    </Button>
                ) : (
                    <LoadingButton
                        color="primary"
                        size="md"
                        ghost
                        onClick={() => handleConnect()}
                        disabled={isDisabled}
                        ref={connectMeButtonRef}
                    >
                        <Box display="flex" gap="xsmall" alignItems="center">
                            <Text weight="bold" color={theme.colors.primaryColor}>
                                Connect me
                            </Text>
                            <Box display="block">
                                <IconUserGroup
                                    color={theme.colors.primaryColor}
                                    style={{margin: 0}}
                                />
                            </Box>
                        </Box>
                    </LoadingButton>
                )}
            </Box>
        </>
    );
}
